import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Award, Download, Share2 } from "lucide-react";
import { useBranding } from "@/lib/branding";
import { toast } from "sonner";
import { openCertificatePdf } from "@/lib/certificateDownload";

export default function Certificate() {
    const { certId } = useParams();
    const [cert, setCert] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const branding = useBranding();

    useEffect(() => {
        api.get(`/certificates/${certId}`)
            .then((r) => setCert(r.data))
            .catch(() => navigate("/"))
            .finally(() => setLoading(false));
    }, [certId, navigate]);

    if (loading)
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">
                Loading certificate…
            </div>
        );
    if (!cert) return null;

    const publicVerifyUrl = `${window.location.origin}/verify/${certId}`;
    const copyShareLink = () => {
        navigator.clipboard.writeText(publicVerifyUrl);
        toast.success("Public verify link copied");
    };

    return (
        <div className="max-w-4xl mx-auto px-6 py-12" data-testid="certificate-page">
            <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-primary">
                ← Back to dashboard
            </Link>
            <Card className="border-border mt-4">
                <CardContent className="p-10 text-center">
                    <Award className="h-12 w-12 text-accent mx-auto" />
                    <div className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mt-6">
                        {branding.institution_name}
                    </div>
                    <h1 className="font-display text-4xl text-primary mt-4">
                        Certificate of Completion
                    </h1>
                    <div className="mt-6 text-muted-foreground">proudly awarded to</div>
                    <div className="font-display text-3xl text-primary mt-3">
                        {cert.student_name}
                    </div>
                    <div className="mt-4 text-muted-foreground">for successfully completing</div>
                    <div className="font-display italic text-xl text-foreground mt-2">
                        {cert.program_title}
                    </div>
                    <div className="mt-8 text-xs text-muted-foreground">
                        Issued {new Date(cert.issued_at).toLocaleDateString()} · Enrollment {cert.enrollment_id}
                    </div>
                    <Button
                        className="mt-8 bg-primary hover:bg-primary/90"
                        data-testid="download-cert-btn"
                        onClick={() => openCertificatePdf(certId, { download: true })}
                    >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                    </Button>
                    <div className="mt-4 flex items-center justify-center gap-2">
                        <Button
                            variant="outline"
                            onClick={copyShareLink}
                            data-testid="share-verify-link-btn"
                        >
                            <Share2 className="h-4 w-4 mr-2" />
                            Copy public verify link
                        </Button>
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground">
                        Share{" "}
                        <span className="font-mono text-foreground/80">
                            /verify/{certId.slice(0, 8)}…
                        </span>{" "}
                        on LinkedIn or your résumé — anyone can confirm authenticity.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
